export * from "common/utils/strings";
import {
  BASE_LANGUAGES,
  PortalLanguage,
  portalLanguageOrDefault,
} from "common/i18nConstants";
import { checkNever } from "./identity";
import { i18n as I18n, TFunction } from "i18next";
import humanizeDuration, {
  SupportedLanguage as HumanizeLanguage,
  Options,
} from "humanize-duration";

export const isString = (input: unknown): input is string =>
  typeof input === "string" || input instanceof String;

/**
 * @deprecated this helper is not i18n-safe; it doesn't use locale-specific case
 * mappings and doesn't obey localized capitalization rules.  it should not be
 * used for user-facing strings, and should be removed whenever you see it.
 */
export const capitalize = (input?: string): string =>
  input ? input.toLowerCase().charAt(0).toUpperCase() + input.slice(1) : "";

/**
 * @deprecated this helper is not i18n-safe; it doesn't use locale-specific case
 * mappings and doesn't obey localized capitalization rules.  it should not be
 * used for user-facing strings, and should be removed whenever you see it.
 */
export const uncapitalize = (input?: string): string =>
  input ? input.slice(0, 1).toLowerCase() + input.slice(1) : "";

/**
 * @deprecated this helper is not i18n-safe; it doesn't use locale-specific case
 * mappings and doesn't obey localized capitalization rules.  it should not be
 * used for user-facing strings, and should be removed whenever you see it.
 */
export const titleCase = (input?: string): string =>
  input
    ? input
        .toLowerCase()
        .split(" ")
        .map((word) => capitalize(word))
        .join(" ")
    : "";

export const snakeToCamel = (input: string): string => {
  if (!input) {
    return "";
  }
  const pascal = snakeToPascal(input);
  const firstLetter = pascal[0];
  if (!firstLetter) {
    return "";
  }
  return firstLetter.toLowerCase() + pascal.slice(1);
};

// we use less readable methods here for performance reasons because this is run
// over all data receive via API
export const snakeToPascal = (input: string): string => {
  const segments = input.split("_");
  let index = segments.length;
  while (index-- > 0) {
    const segment = segments[index];
    if (!segment || segment.length === 0) {
      continue;
    }
    const firstLetter = segment[0];
    if (!firstLetter) {
      continue;
    }
    segments[index] = firstLetter.toUpperCase() + segment.slice(1);
  }
  return "".concat(...segments);
};

export const camelToSnake = (input?: string): string =>
  input
    ? input.replaceAll(/[A-Z]/g, (letter: string) => `_${letter.toLowerCase()}`)
    : "";

export const toHumanizeLanguage = (
  language: PortalLanguage
): // Note: Humanize actually *does* support sr-Latn, the types module just doesn't reflect this yet. `| "sr_Latn"` can be removed at types 1.33 or later
HumanizeLanguage | "sr_Latn" | undefined => {
  switch (language) {
    case "bs-Latn-BA": {
      return undefined; // humanize-duration doesn't support bosnian
    }
    case "da-DK":
    case "de-DE":
    case "en-US":
    case "es-ES":
    case "fr-FR":
    case "it-IT":
    case "lt-LT":
    case "nl-NL":
    case "pl-PL":
    case "ro-RO":
    case "sv-SE":
    case "uk-UA": {
      return BASE_LANGUAGES[language];
    }
    case "sr-Latn-RS": {
      return "sr_Latn";
    }
    default: {
      checkNever(language);
      return language; // should be unreachable
    }
  }
};

export const formatDuration = (
  milliseconds: number | undefined,
  i18n: I18n,
  t: TFunction,
  options: Options = {}
): string => {
  const supportedLanguage = toHumanizeLanguage(
    portalLanguageOrDefault(i18n.language)
  );
  const humanizeOpts = {
    largest: options.largest ?? 1,
    round: options.round ?? false,
    maxDecimalPoints: options.maxDecimalPoints ?? 1,
    ...options,
  };
  // If the language is supported directly by humanize-duration, use that, as they are likely to have gotten the pluralization nuances correct.
  if (supportedLanguage) {
    return humanizeDuration(milliseconds ?? 0, {
      language: supportedLanguage,
      ...humanizeOpts,
    });
  } else {
    const currentDict: humanizeDuration.UnitTranslationOptions = {
      y: (count?: number) => t("utils.durations.year", { count }),
      mo: (count?: number) => t("utils.durations.month", { count }),
      w: (count?: number) => t("utils.durations.week", { count }),
      d: (count?: number) => t("utils.durations.day", { count }),
      h: (count?: number) => t("utils.durations.hour", { count }),
      m: (count?: number) => t("utils.durations.minute", { count }),
      s: (count?: number) => t("utils.durations.second", { count }),
      ms: (count?: number) => t("utils.durations.millisecond", { count }),
    };
    const humanizer = humanizeDuration.humanizer({
      language: i18n.language,
      languages: {
        [i18n.language]: currentDict,
      },
      ...humanizeOpts,
    });

    return humanizer(milliseconds ?? 0);
  }
};

/**
 * Locale-aware string comparison for sorting UI lists.
 * `numeric: true` sorts "item2" before "item10"; `sensitivity: "base"`
 * ignores case and accents so "Éclair" and "eclair" compare equal.
 */
export const localeCompare = (a: string, b: string, locale: string): number =>
  a.localeCompare(b, locale, { numeric: true, sensitivity: "base" });

/** Fold case and accents so a search for "Babe" matches "Babé". */
export const normalizeAccentsAndCase = (s: string): string =>
  s
    .normalize("NFD")
    .replaceAll(/\p{Diacritic}/gu, "")
    .toLowerCase();
